import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteDepartment, fetchDepartments } from '../departmentsApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './BoardPage.css';

const PAGE_SIZE = 10;

export default function DepartmentsPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { selectedBranch: globalBranch } = useBranchFilter();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const data = await fetchDepartments();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('departments.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveBranch = globalBranch !== 'all' ? globalBranch : branchFilter;

  const filtered = useMemo(() => rows.filter((x) => {
    const q = search.trim().toLowerCase();
    const qMatch = !q
      || x.name.toLowerCase().includes(q)
      || (x.managerName || '').toLowerCase().includes(q)
      || (x.branch || '').toLowerCase().includes(q)
      || (x.parentName || '').toLowerCase().includes(q);
    const bMatch = effectiveBranch === 'all' || x.branch === effectiveBranch;
    return qMatch && bMatch;
  }), [rows, search, effectiveBranch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = rows.filter((x) => x.isActive).length;

  const openDelete = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDepartment(deleteTarget.id);
      setRows((prev) => prev.map((x) => (x.id === deleteTarget.id ? { ...x, isActive: false } : x)));
      setConfirmOpen(false); setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('departments.deleteFailed'));
      setConfirmOpen(false); setDeleteTarget(null);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="board" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('departments.title')}</span>
            <span className="list-page-card__sub">{t('departments.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button className="btn btn--primary" type="button" onClick={() => navigate(`${basePath}/departments/new`)}><Icon name="plus" size={14} color="#fff" /> {t('departments.add')}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('departments.loading')}</div>}

      {!loading && (
        <>
          <div className="page__stats">
            <div className="page__stat-chip page__stat-chip--total"><Icon name="board" size={13} /> {t('common.total')}: {rows.length}</div>
            <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t('common.active')}: {activeCount}</div>
            <div className="page__stat-chip page__stat-chip--new"><Icon name="x-circle" size={13} /> {t('common.inactive')}: {rows.length - activeCount}</div>
          </div>

          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" placeholder={t('departments.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="page__filter-select" value={branchFilter} onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }}>
              <option value="all">{t('departments.allBranches')}</option>
              {Array.from(new Set(rows.map((x) => x.branch))).filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('departments.colName')}</th>
                  <th>{t('departments.colParent')}</th>
                  <th>{t('departments.colMgr')}</th>
                  <th>{t('departments.colSubs')}</th>
                  <th>{t('departments.colBranch')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-state__title">{t('departments.noMatch')}</div><div className="empty-state__sub">{t('departments.noMatchSub')}</div></div></td></tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td><strong style={{ color: '#00334a' }}>{item.name}</strong></td>
                    <td>{item.parentName || '—'}</td>
                    <td>{item.managerName || '—'}</td>
                    <td>{item.subDepartmentsCount}</td>
                    <td>{item.branch || '—'}</td>
                    <td><span className={`users-status users-status--${item.isActive ? 'active' : 'inactive'}`}>{item.isActive ? t('common.active') : t('common.inactive')}</span></td>
                    <td>
                      <div className="td-actions">
                        <button type="button" className="action-btn action-btn--edit" onClick={() => navigate(`${basePath}/departments/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="action-btn action-btn--delete" onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > PAGE_SIZE && (
              <div className="pagination">
                <span>{t('common.showing')} {Math.min(filtered.length, (page - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, page * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
                <div className="pagination__pages">
                  <button type="button" className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => <button type="button" key={p} className={`pagination__btn${page === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
                  <button type="button" className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
        title={t('departments.confirmDeactivate')}
        confirmLabel={t('departments.deactivate')}
        message={<>{t('departments.colName')}: <strong>{deleteTarget?.name}</strong></>}
      />
    </div>
  );
}
